(ns hai.home.mobile.onboarding
  "Full-screen, multi-screen onboarding flow for the mobile web-app. Opens by
   itself once per user (see [[::auto-open]]), and after that from the Tips
   and tricks card via [[hai.home.mobile.state/open-onboarding]]; the user steps
   through [[screens]] with \"← Back\" / Next, or dismisses it with the X.
   The only thing persisted is finishing it: \"Go to Humble\" on the last
   screen stamps the Tips and tricks card's \"Take the mobile tour\" step (the
   X doesn't). The task, links and cards on the later screens are
   illustrations, not the real forms."
  #?(:cljs (:require-macros hai.home.mobile.onboarding))
  (:require
   #?(:clj [garden.stylesheet])
   [hai.ui.components.new-design.utils :refer [px->rem]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [hai.ui.icons :as icons]
   ;; cljs-only aliases: the clj pass (the :require-macros self-load, for
   ;; Ornament) still reads the cljs branches, so keywords here are written
   ;; out in full — `::state/x` fails to read in clj, where `state` is unknown.
   #?@(:cljs [[hai.home.mobile.state :as state]
              [hai.home.onboarding :as home-onboarding]
              [hai.ui.rf-sugar :refer [<s >e defevent-fx]]
              [reagent.core :as r]])))

#?(:cljs (def ^:private close-icon (icons/mui-icon :outlined :close)))
#?(:cljs (def ^:private link-icon (icons/mui-icon :outlined :link)))
#?(:cljs (def ^:private person-icon (icons/mui-icon :outlined :person)))
#?(:cljs (def ^:private note-icon (icons/mui-icon :outlined :description)))
#?(:cljs (def ^:private mail-icon (icons/mui-icon :outlined :mail)))
#?(:cljs (def ^:private replay-icon (icons/mui-icon :outlined :replay)))

;; The task the illustrations are about, typed out on the Start screen and
;; carried through the ones after it.
#?(:cljs (def ^:private example-task "Prepare proposal for John"))

;; Progress dots cover the "try it" stretch of the flow (Start → Check email).
#?(:cljs (def ^:private progress-steps 5))

;; ---------------------------------------------------------------------------
;; Styled pieces — frame
;; ---------------------------------------------------------------------------

(o/defstyled shell :div
  {:position "fixed" :inset "0"
   ;; Above the drawer (100) and FAB (110).
   :z-index "120"
   :background t/--colour-ink
   :display "flex" :flex-direction "column"
   :padding "8px 14px calc(16px + env(safe-area-inset-bottom))"}
  [:.in {:color t/--colour-pink :text-transform "uppercase" }]
  [:.welcome {:color t/--colour-white :font-size (px->rem 30)
              :font-weight 800 :padding "10px 60px" :line-height "1.2"}]
  [:.onespace {:color t/--colour-white :font-size (px->rem 20)
               :font-weight 700}]
  ;; The illustrations only add motion; with reduced motion they sit in their
  ;; final state (every animation below ends on its element's base style).
  [:at-media {:prefers-reduced-motion "reduce"}
   [:* {:animation "none !important"}]])

(o/defstyled header :header
  {:display "flex" :align-items "center" :justify-content "space-between"
   :flex "0 0 auto" :padding "2em"
   :min-height "48px"}
  [:.back
   {:border "none" :background "transparent" :padding "0"
    :font-family "inherit" :font-size "15px" :font-weight "500"
    :color t/--colour-white
    :cursor "pointer"}]
  [:.close
   {:width "32px" :height "32px"
    ;; Stays pinned right when the Back button isn't rendered (first step).
    :margin-left "auto"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :background "transparent" :border-radius "10px"
    :cursor "pointer" :color t/--colour-white}
   [:svg {:width "18px" :height "18px" :fill "currentColor"}]])

(o/defstyled progress-dots :div
  {:flex "0 0 auto"
   :width "140px" :margin "0 auto 8px"
   :display "flex" :justify-content "space-between"}
  [:span {:width "9px" :height "9px" :border-radius "50%"
          :background t/--colour-rock}]
  [:.done {:background t/--colour-pink}])

(o/defstyled content :main
  {:flex "1 1 auto"
   :overflow-y "auto"
   :display "flex" :flex-direction "column"
   :align-items "center" :justify-content "center"
   :gap "16px"
   :padding "24px 12px"
   :text-align "center"}
  ;; The screens that walk through a task read top-down, left-aligned.
  [:&.top {:align-items "stretch" :justify-content "flex-start"
           :gap "0" :padding "8px 20px 0"
           :text-align "left"}
   ;; Once a screen outgrows the viewport, scroll rather than squash: a
   ;; shrunk card clips its own bottom (Save) under `overflow: hidden`.
   [:>* {:flex-shrink "0"}]])

(o/defstyled footer :footer
  {:flex "0 0 auto"
   :padding "2em"}
  [:.next
   {:width "100%"
    :padding "14px 20px"
    :border "none" :border-radius "14px"
    :background t/--colour-pink
    :color t/--colour-ink
    :font-family "inherit" :font-size "16px" :font-weight "700"
    :cursor "pointer"}])

;; ---------------------------------------------------------------------------
;; Styled pieces — illustrations
;; ---------------------------------------------------------------------------

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules onboarding-keyframes
  (garden.stylesheet/at-keyframes
   :onboarding-pulse-ring
   ["0%"   {:box-shadow "0 0 0 0 rgba(71,5,208,0.35)"}]
   ["50%"  {:box-shadow "0 0 0 5px rgba(71,5,208,0.12)"}]
   ["100%" {:box-shadow "0 0 0 0 rgba(71,5,208,0.35)"}])
  (garden.stylesheet/at-keyframes
   :onboarding-fade-slide
   ["0%"   {:opacity "0" :transform "translateX(-6px)"}]
   ["100%" {:opacity "1" :transform "translateX(0)"}])
  (garden.stylesheet/at-keyframes
   :onboarding-fade
   ["0%"   {:opacity "0"}]
   ["100%" {:opacity "1"}])
  (garden.stylesheet/at-keyframes
   :onboarding-caret-blink
   ["0%"   {:opacity "1"}]
   ["50%"  {:opacity "0"}]
   ["100%" {:opacity "1"}])
  (garden.stylesheet/at-keyframes
   :onboarding-slot-open
   ["0%"   {:grid-template-rows "0fr"}]
   ["100%" {:grid-template-rows "1fr"}])
  (garden.stylesheet/at-keyframes
   :onboarding-touch-in
   ["0%"   {:opacity "0" :translate "40px 30px"}]
   ["100%" {:opacity "1" :translate "0 0"}])
  (garden.stylesheet/at-keyframes
   :onboarding-draw
   ["0%"   {:stroke-dashoffset "101"}]
   ["100%" {:stroke-dashoffset "0"}]))

(o/defstyled eyebrow :div
  {:margin-bottom "14px"
   :font-size "13px" :font-weight "700"
   :letter-spacing "0.06em" :text-transform "uppercase"
   :color t/--colour-pink})

(o/defstyled lede :p
  {:margin "8px 0"
   :max-width "32ch"
   :font-size "18px" :font-weight "600" :line-height "1.5"
   :color t/--colour-white})

(o/defstyled sub :p
  {:margin "0 0 24px"
   :max-width "32ch"
   :font-size "15px" :line-height "1.5"
   :color "rgba(255,255,255,0.7)"})

(o/defstyled hint :p
  {:margin "14px 0 0"
   :font-size "13px" :line-height "1.5"
   :color "rgba(255,255,255,0.55)"}
  [:&.em {:color t/--colour-pink :font-weight "600"}])

;; A task / note / contact card as it looks in the app: tinted header over a
;; Snow body. Illustration only — nothing in it responds to taps.
(o/defstyled demo-card :div
  {:border-radius "16px" :overflow "hidden"
   :background t/--colour-snow
   :color t/--note-kraft}
  [:.card-head
   {:display "flex" :align-items "center" :gap "10px"
    :padding "14px 16px"
    :font-size "16px" :font-weight "700"}
   [:svg {:width "20px" :height "20px" :fill "currentColor" :flex-shrink "0"}]
   [:.x {:margin-left "auto" :font-size "18px" :line-height "1"}]]
  [:&.task [:.card-head {:background t/--tasks}]]
  [:&.note [:.card-head {:background t/--notes}]]
  [:&.contact [:.card-head {:background t/--contact}]]
  [:.card-body {:padding "20px 16px"}]
  [:.task-text
   {:margin "0"
    :min-height "1.5em"
    :padding "16px 18px"
    :border "1.5px solid var(--tasks)" :border-radius "14px"
    :font-size "16px" :line-height "1.5"}]
  ;; An opened task's title, as the task form shows it read-only
  ;; (`hai.tasks.form` .title-display).
  [:.task-title
   {:margin "0" :padding "0 2px"
    :font-size "22px" :font-weight "700" :line-height "1.25"
    :letter-spacing "-0.01em"
    :color t/--card-forest
    :overflow-wrap "anywhere"}]
  [:.placeholder {:color "rgba(50,64,37,0.45)"}]
  [:.caret
   {:display "inline-block"
    :width "2px" :height "1.1em"
    :margin-left "1px" :vertical-align "text-bottom"
    :background "currentColor"
    :animation "onboarding-caret-blink 1s step-end infinite"}]
  [:.rows {:display "flex" :flex-direction "column" :gap "10px" :margin-top "16px"}]
  ;; Connect's growing list: no flex gap — each item's spacing lives inside
  ;; its slot, so a slot opening from nothing (0fr → 1fr) slides everything
  ;; below it, the "Add link" included, down smoothly instead of jumping.
  [:.link-list {:display "flex" :flex-direction "column" :margin-top "16px"}]
  [:.slot {:display "grid" :grid-template-rows "1fr"
           :animation "onboarding-slot-open 0.3s ease-out both"}
   [:>div {:min-height "0" :overflow "hidden" :padding-bottom "10px"}]]
  [:.row {:display "flex" :align-items "center" :gap "10px"
          :flex-wrap "wrap" :min-height "38px"}]
  [:.card-foot {:display "flex" :justify-content "flex-end" :align-items "center"
                :padding "0 16px 16px"}]
  [:.save
   {:display "flex" :align-items "center" :gap "8px"
    :padding "10px 20px" :border-radius "10px"
    :background t/--colour-purple :color t/--colour-white
    :border "none" :font-family "inherit"
    :font-size "14px" :font-weight "700"
    :cursor "pointer"
    :transition "background 0.2s"}
   [:.key {:display "flex" :align-items "center" :justify-content "center"
           :width "22px" :height "22px" :border-radius "6px"
           :font-size "13px"}]
   [:&.disabled {:background t/--colour-rock :cursor "default"}
    [:.key {:background "rgba(255,255,255,0.35)"}]]])

(o/defstyled pill :span
  {:display "inline-flex" :align-items "center" :gap "8px"
   :width "fit-content"
   :padding "8px 14px" :border-radius "999px"
   :background t/--tasks
   :color t/--note-kraft
   :font-size "13px" :font-weight "600"
   :transition "scale 0.12s ease-out"}
  [:svg {:width "15px" :height "15px" :fill "currentColor" :flex-shrink "0"}]
  [:&.dashed {:background "transparent"
              :border "1.5px dashed rgba(50,64,37,0.5)"}]
  ;; Only while an animation is about to fill the pill — a lone pulsing pill
  ;; reads as tappable, and none of these are.
  [:&.pulse {:animation "onboarding-pulse-ring 1.1s ease-in-out infinite"}]
  [:&.enter {:animation "onboarding-fade-slide 0.4s ease-out both"}]
  [:&.pressed {:scale "0.95"}]
  [:&.small {:padding "6px 12px" :font-size "12px"}
   [:svg {:width "13px" :height "13px"}]])

;; A pretend tap on its child: the translucent disc a device emulator shows
;; for a touch, gliding in from below-right, then pressing. Motion uses the
;; `translate` / `scale` properties so the entry animation and the press
;; don't fight over `transform`.
(o/defstyled touch-target :span
  {:position "relative" :display "inline-block"}
  [:.touch
   {:position "absolute" :left "70%" :top "60%"
    :width "28px" :height "28px" :margin "-14px 0 0 -14px"
    :border-radius "50%"
    :background "rgba(0,0,0,0.3)"
    :pointer-events "none"
    :transition "scale 0.12s ease-out, background 0.12s ease-out"
    :animation "onboarding-touch-in 0.6s ease-out 0.4s both"}
   [:&.pressed {:scale "0.8" :background "rgba(0,0,0,0.45)"}]])

(o/defstyled pill-hint :span
  {:font-size "12px" :font-weight "700"
   :white-space "nowrap"
   :color t/--colour-ink
   ;; Held back so the linked item lands first and the hint reads as its
   ;; caption, not part of the same pop-in. A plain fade — it doesn't move.
   :animation "onboarding-fade 0.5s ease-out 1.1s both"})

;; A loose oval drawn around its child as if by hand with a purple pen: open
;; ended, overshooting its start, tipped a few degrees. `pathLength` 100 lets
;; the dash animation draw it on regardless of the child's size — which rules
;; out `vector-effect: non-scaling-stroke`: that measures dashes in screen
;; pixels, ignoring `pathLength`. The viewBox keeps roughly the circled pill's
;; proportions instead, so the stroke doesn't visibly stretch.
(o/defstyled hand-circle :span
  {:position "relative" :display "inline-block"}
  ;; Scoped to the pen's own svg — a bare `svg`/`path` would also restyle the
  ;; icon inside the circled child.
  [:>svg.pen {:position "absolute" :inset "-10px -14px"
              :width "calc(100% + 28px)" :height "calc(100% + 20px)"
              :overflow "visible"
              :pointer-events "none"
              :transform "rotate(-4deg)"}
   [:path {:fill "none"
           :stroke t/--colour-purple :stroke-width "1.9"
           :stroke-linecap "round" :stroke-linejoin "round"
           ;; One dash the length of the path, a gap a little longer: sliding
           ;; the offset 101 → 0 draws it from nothing to closed. Starting at
           ;; exactly 100 would park the dash's end on the path's start, where
           ;; the round cap shows as a dot while it waits.
           :stroke-dasharray "100 102" :stroke-dashoffset "0"
           :animation "onboarding-draw 0.6s ease-out both"}]]
  ([{:keys [delay]} child]
   [:<>
    child
    [:svg.pen {:viewBox "0 0 100 40" :preserveAspectRatio "none" :aria-hidden true}
     [:path {:d "M88 8C70 0 22 1 8 12C-2 21 10 36 44 38C77 39 100 31 97 17C95 8 80 3 60 5"
             :pathLength 100
             :style {:animation-delay delay}}]]]))

;; Sits left in the card's footer, opposite Save.
(o/defstyled replay :button
  {:display "inline-flex" :align-items "center" :gap "6px"
   :margin-right "auto" :padding "6px 0"
   :border "none" :background "transparent"
   :color t/--colour-purple
   :font-family "inherit" :font-size "14px" :font-weight "700"
   :cursor "pointer"}
  [:svg {:width "18px" :height "18px" :fill "currentColor"}])

(o/defstyled mail-badge :div
  {:display "flex" :align-items "center" :justify-content "center"
   :width "64px" :height "64px" :border-radius "18px"
   :margin-bottom "8px"
   :background t/--colour-purple}
  [:svg {:width "28px" :height "28px" :fill t/--colour-pink}])

;; ---------------------------------------------------------------------------
;; Screens
;; ---------------------------------------------------------------------------

#?(:cljs
(defn- reduced-motion? []
  (and (exists? js/matchMedia)
       (.-matches (js/matchMedia "(prefers-reduced-motion: reduce)")))))

#?(:cljs
(defn- welcome []
  [:<>
   [icons/humble-logo-squidge-dark {:width 128
                                    :height 128}]
   [:div.in "You're in"]
   [:div.welcome "Welcome to Humble"]
   [:div.onespace "One connected space" [:br] "for your" [:br] "everyday life and work."]]))

#?(:cljs
(defn- save-button
  "The pretend form's Save. Nothing is saved; once enabled, it moves the flow
   on like the footer's Next."
  [disabled?]
  (let [step @(<s [:hai.home.mobile.state/onboarding-step])]
    [:button.save {:type     "button"
                   :class    (when disabled? "disabled")
                   :disabled disabled?
                   :on-click #(>e [:hai.home.mobile.state/set-onboarding-step (inc step)])}
     "Save " [:span.key {:aria-hidden true} "↵"]])))

#?(:cljs
(defn- start
  "The Add-a-task form typing out [[example-task]], Save coming alive as the
   text arrives."
  []
  (r/with-let [n (count example-task)
               !typed (r/atom (if (reduced-motion?) n 0))
               !timer (atom nil)
               tick (fn tick []
                      (when (< @!typed n)
                        (reset! !timer (js/setTimeout #(do (swap! !typed inc) (tick)) 80))))
               ;; A beat on the empty form, Save disabled, before typing starts.
               _ (reset! !timer (js/setTimeout tick 1500))]
    (let [typed @!typed
          done? (= typed n)]
      [:<>
       [eyebrow "Let's start with your work"]
       [lede "What's something you're working on?"]
       [sub "Create a task for something you'll want to come back to."]
       ;; Hidden from screen readers piece by piece, leaving Save (a real
       ;; button) reachable.
       [demo-card {:class "task"}
        [:div.card-head {:aria-hidden true}
         [icons/humble-task {:size 20}] "Add a task" [:span.x "✕"]]
        [:div.card-body {:aria-hidden true}
         [:p.task-text
          (if (zero? typed)
            ;; Caret sits before the placeholder, as in an empty input.
            [:<> [:span.caret] [:span.placeholder "What's the task?"]]
            [:<> (subs example-task 0 typed) (when-not done? [:span.caret])])]]
        [:div.card-foot [save-button (zero? typed)]]]
       [hint "This works the same on desktop and in your browser."]])
    (finally
      (js/clearTimeout @!timer)))))

;; What the Connect screen links to its task, in order.
#?(:cljs
(def ^:private connect-items
  [{:icon link-icon :label "pricingresearch.com" :hint "← add a bookmark"}
   {:icon person-icon :label "john.chan@client.com" :hint "← or a contact"}
   {:icon note-icon :label "Proposal planning" :hint "← or a note"}
   {:icon icons/humble-task :label "Translate proposal" :hint "← or a related task"}]))

;; The Connect animation, one step per phase of each item's "Add link":
;; :idle — sitting still (1s; 2s after the first, a 1s pause to read the item
;; just linked plus the 1s still); :pulse — pulsing as the touch glides in;
;; :press — the tap, after which the item takes the button's place and a new
;; button appears below. Ends on :done — every item linked, the button at rest.
#?(:cljs
(def ^:private connect-timeline
  (conj (vec (for [i (range (count connect-items))
                   [phase ms] [[:idle (if (zero? i) 1000 2000)]
                               [:pulse 1300]
                               [:press 250]]]
               {:item i :phase phase :ms ms}))
        {:item (count connect-items) :phase :done})))

#?(:cljs
(defn- linked-row
  "A linked item: its slot opens, pushing \"Add link\" down, then the item
   unfolds into the space."
  [{:keys [icon label hint]}]
  [:div.slot
   [:div
    [:div.row
     [pill {:class "enter" :style {:animation-delay "0.3s"}} [icon] label]
     [pill-hint hint]]]]))

#?(:cljs
(defn- add-link-row
  "The one \"Add link\", always last in the list. One element throughout
   (the caller keys it stably), so it's pushed down rather than re-entering."
  [{:keys [phase]}]
  (let [touching? (#{:pulse :press} phase)
        pressed? (= phase :press)]
    [:div.row
     [touch-target
      [pill {:class (cond-> ["dashed"]
                      touching? (conj "pulse")
                      pressed? (conj "pressed"))}
       [link-icon] "Add link"]
      (when touching?
        [:span.touch {:class (when pressed? "pressed")}])]])))

#?(:cljs
(defn- connect
  "The task picking up a link, a contact, a note and another task, the
   \"Add link\" button tapped each time and staying on below the items —
   played once, with Replay to see it again."
  []
  (r/with-let [reduced? (reduced-motion?)
               last-step (dec (count connect-timeline))
               !step (r/atom (if reduced? last-step 0))
               !timer (atom nil)
               play! (fn []
                       (js/clearTimeout @!timer)
                       (reset! !step 0)
                       ((fn advance []
                          (when-let [ms (:ms (nth connect-timeline @!step))]
                            (reset! !timer
                                    (js/setTimeout #(do (swap! !step inc) (advance)) ms))))))
               _ (when-not reduced? (play!))]
    (let [{:keys [item phase] :as step} (nth connect-timeline @!step)]
      [:<>
       [eyebrow "Add context as you work"]
       [lede "Task in Humble is structured differently, you can..."]
       ;; Hidden from screen readers piece by piece, not as a whole card, so
       ;; Replay (a real button, sharing Save's row to stay above the fold)
       ;; stays reachable.
       [demo-card {:class "task"}
        [:div.card-head {:aria-hidden true}
         [icons/humble-task {:size 20}] "Add a task" [:span.x "✕"]]
        [:div.card-body {:aria-hidden true}
         [:p.task-text example-task]
         (-> [:div.link-list]
             (into (map-indexed (fn [i linked] ^{:key i} [linked-row linked]))
                   (take item connect-items))
             (conj ^{:key :add-link} [add-link-row step]))]
        [:div.card-foot
         (when (and (= phase :done) (not reduced?))
           [replay {:type "button" :on-click play!}
            [replay-icon] "Replay"])
         [save-button false]]]
       [hint {:class "em"} "Tap “Add link” to connect something relevant to the task."]])
    (finally
      (js/clearTimeout @!timer)))))

#?(:cljs
(defn- your-task []
  [:<>
   [eyebrow "Pick up where you left off"]
   [lede "Everything you connected stays with your task."]
   [sub "So when you come back, you don't have to reconstruct everything."]
   [demo-card {:class "task" :aria-hidden true}
    [:div.card-head [icons/humble-task {:size 20}] "Task"]
    [:div.card-body
     ;; A saved task, so its title reads as the heading, not an input.
     [:h2.task-title example-task]
     [:div.rows
      [pill [link-icon] "pricingresearch.com"]
      [pill [person-icon] "john.chan@client.com"]
      [pill [note-icon] "Proposal planning"]
      [pill [icons/humble-task] "Translate proposal"]]]]]))

#?(:cljs
(defn- mini-card [kind icon title text delay]
  [demo-card {:class kind}
   [:div.card-head icon title]
   [:div.card-body
    [:p {:style {:margin "0 0 12px" :font-size "15px"}} text]
    [hand-circle {:delay delay}
     [pill {:class ["dashed" "small"]} [link-icon] "Add link"]]]]))

#?(:cljs
(defn- same-for-all []
  [:<>
   [eyebrow "Keep your work organised"]
   [lede "The same idea works across Humble."]
   [sub "Connect what matters to your Tasks, Notes and Contacts."]
   ;; One by one, Task → Note, each "Add link" gets circled.
   [:div {:aria-hidden true
          :style {:display "flex" :flex-direction "column" :gap "12px"}}
    [mini-card "task" [icons/humble-task {:size 20}] "Task" example-task "1.2s"]
    [mini-card "note" [note-icon] "Note" "Proposal planning thoughts" "2s"]
    ;; Contacts don't take links yet — bring this back when they do.
    #_[mini-card "contact" [person-icon] "Contact" "John Chan" "2.1s"]]]))

#?(:cljs
(defn- check-email []
  [:<>
   [mail-badge [mail-icon]]
   [lede "Check your emails for updates as we share with you different ways to work with Humble."]]))

;; :layout — :centered (default) or :top; :progress — the lit dot count, on
;; the screens that show dots; :cta — footer label, "Next" when absent.
#?(:cljs
(def screens
  [{:id :welcome
    :title "Welcome to Humble"
    :cta "Get started"
    :body welcome}
   {:id :start
    :title "Start with your work"
    :layout :top
    :progress 1
    :body start}
   {:id :connect
    :title "Add context as you work"
    :layout :top
    :progress 2
    :cta "Next →"
    :body connect}
   {:id :your-task
    :title "Pick up where you left off"
    :layout :top
    :progress 3
    :body your-task}
   {:id :same-for-all
    :title "Keep your work organised"
    :layout :top
    :progress 4
    :body same-for-all}
   {:id :check-email
    :title "Check your email"
    :progress 5
    :cta "Go to Humble"
    :body check-email}]))

;; ---------------------------------------------------------------------------
;; Views
;; ---------------------------------------------------------------------------

#?(:cljs
(defn- close! [] (>e [:hai.home.mobile.state/close-onboarding])))

;; The last screen's CTA: the tour is done, so its checklist step is too.
#?(:cljs
(defn- finish! []
  (>e [:hai.home.onboarding/complete-step :onboarding.step/mobile-tour])
  (close!)))

#?(:cljs
(defn- flow
  "Mounted only while the flow is open, so the ref callback locks the body
   scroll for exactly that lifetime (same approach as the note editor's
   full-screen surface)."
  [step]
  (r/with-let [!body-overflow (atom nil)
               lock-body-scroll! (fn [el]
                                   (let [body-style (.. js/document -body -style)]
                                     (if el
                                       (do (reset! !body-overflow (.-overflow body-style))
                                           (set! (.-overflow body-style) "hidden"))
                                       (set! (.-overflow body-style) @!body-overflow))))]
    (let [total (count screens)
          {:keys [id title layout progress cta body]} (nth screens step)
          first? (zero? step)
          last?  (= step (dec total))]
      [shell {:ref        lock-body-scroll!
              :role       "dialog"
              :aria-modal "true"
              :aria-label (str "Onboarding, step " (inc step) " of " total ": " title)}
       [header
        (when-not first?
          [:button.back
           {:type     "button"
            :on-click #(>e [:hai.home.mobile.state/set-onboarding-step (dec step)])}
           "← Back"])
        [:button.close
         {:type       "button"
          :aria-label "Close onboarding"
          :on-click   close!}
         [close-icon]]]
       (when progress
         (into [progress-dots {:aria-hidden true}]
               (for [i (range 1 (inc progress-steps))]
                 [:span {:class (when (<= i progress) "done")}])))
       ;; Keyed by screen, so each one mounts fresh and replays its animation.
       ^{:key id}
       [content {:class (when (= layout :top) "top")}
        [body]]
       [footer
        [:button.next
         {:type     "button"
          :on-click (if last?
                      finish!
                      #(>e [:hai.home.mobile.state/set-onboarding-step (inc step)]))}
         (or cta "Next")]]]))))

;; Opens the tour by itself the first time a signed-in user reaches the mobile
;; web-app — wherever they signed up, since "seen" is per user, not per device
;; — and marks it seen at once, so it never does so again however it ends.
#?(:cljs
(defevent-fx ::auto-open [{:keys [db]}]
  (when (and (get-in db [:auth/identity :logged-in?])
             (not (home-onboarding/mobile-tour-seen? db))
             (nil? (:hai.home.mobile.state/onboarding-step db)))
    {:fx [[:dispatch [:hai.home.mobile.state/open-onboarding]]
          [:dispatch [:hai.home.onboarding/mark-mobile-tour-seen]]]})))

#?(:cljs
(defn overlay
  "The tour, while open. Mounted by the mobile chrome for its lifetime, it also
   watches for sign-in (the identity loads after the page) to auto-open the
   tour the first time — see [[::auto-open]]."
  []
  (r/with-let [watch (r/track! #(when @(<s [:auth/logged-in?])
                                  (>e [::auto-open])))]
    (when-let [step @(<s [:hai.home.mobile.state/onboarding-step])]
      [flow step])
    (finally
      (r/dispose! watch)))))
